import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { SecondaryButton } from 'components/Shared/Button'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import { uploadLibraryImages } from 'api/library'
import { computeAspectRatio } from 'components/Recommendations/Campaign/Media/media'
import { getMediaDimensions } from 'utils/helpers'
import { LIBRARY_TABS } from './constant'
import LibraryTabs from './element/LibraryTabs'
import SearchbarInput from '../Shared/Searchbar'
import searchLogo from '../../images/icons/search_blue.svg'
import FilterDropdown from './element/FilterDropdown'
import { TransparentBgButton } from 'components/Shared/Button'
import { LibraryList } from './element/LibraryList'
import { useLibraryList } from './element/LibraryList/useLibraryList'
import { useLibraryFilters } from './hooks/useLibraryFilters'

const Library = () => {
  const [activeTab, setActiveTab] = useState(LIBRARY_TABS.ALL)
  const [selectiveMode, setSelectiveMode] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [countsRefreshKey, setCountsRefreshKey] = useState(0)

  const refreshCounts = () => {
    setCountsRefreshKey((key) => key + 1)
  }

  const fileInputRef = useRef(null)
  const uploadCancelSourceRef = useRef(null)
  const { t } = useTranslation()
  const { addNotification } = useToast()
  const filters = useLibraryFilters()
  const libraryList = useLibraryList({
    activeTab,
    search: filters.search,
    sort: filters.sort,
    ratios: filters.ratios,
  })

  useEffect(() => {
    return () => {
      uploadCancelSourceRef.current?.cancel('Library unmounted')
    }
  }, [])

  const handleUploadClick = () => {
    if (isUploading) {
      return
    }

    fileInputRef.current?.click()
  }

  const validateFile = async (file) => {
    const errorMessages = []

    // Image File Validation
    if (file.type.startsWith('image/')) {
      // Check size (Max 30 MB)
      if (file.size > 30 * 1024 * 1024) {
        errorMessages.push(t('common.errors.fileErrors.imageSize', { fileName: file.name }))
      }

      // Check type (JPG, PNG)
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        errorMessages.push(t('common.errors.fileErrors.imageType', { fileName: file.name }))
      }

      await new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          const { width, height } = img
          const aspectRatio = width / height
          const isverticalFormat = aspectRatio >= 0.5 && aspectRatio <= 0.575 && height > width
          // Reel,story image - resolution validation
          if (isverticalFormat && (width > 1080 || height > 1920)) {
            errorMessages.push(
              t('common.errors.fileErrors.imageResolution', { fileName: file.name }),
            )
          }
          resolve()
        }
        img.src = URL.createObjectURL(file)
      })
    }

    // Video File Validation
    if (file.type.startsWith('video/')) {
      // Check size (Max 4 GB for videos)
      if (file.size > 4 * 1024 * 1024 * 1024) {
        errorMessages.push(t('common.errors.fileErrors.videoSize', { fileName: file.name }))
      }

      // Check type (MP4, MOV)
      if (!['video/mp4', 'video/quicktime'].includes(file.type)) {
        errorMessages.push(t('common.errors.fileErrors.videoType', { fileName: file.name }))
      }

      await new Promise((resolve) => {
        const video = document.createElement('video')
        video.onloadedmetadata = () => {
          const { videoWidth, videoHeight } = video
          const aspectRatio = videoWidth / videoHeight
          const isverticalFormat =
            aspectRatio >= 0.5 && aspectRatio <= 0.575 && videoHeight > videoWidth
          // Feed validation
          if (video.duration > 240 * 60) {
            errorMessages.push(
              t('common.errors.fileErrors.videoDurationFeed', { fileName: file.name }),
            )
          }
          // Reel, Story validation
          if (isverticalFormat && video.duration > 2 * 60) {
            errorMessages.push(
              t('common.errors.fileErrors.videoDurationReelStory', { fileName: file.name }),
            )
          }

          resolve()
        }
        video.src = URL.createObjectURL(file)
      })
    }

    return errorMessages.length > 0 ? errorMessages : null
  }

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''

    if (!files.length) {
      return
    }

    const validFiles = []
    const errorMessages = []

    for (const file of files) {
      const validationErrors = await validateFile(file)
      if (validationErrors) {
        errorMessages.push(...validationErrors)
      } else {
        validFiles.push(file)
      }
    }

    if (errorMessages.length > 0) {
      addNotification({
        message: t('Library.upload.toastError.title'),
        description: errorMessages.map((message, index) => <div key={index}>{message}</div>),
        variant: 'error',
      })
    }

    if (!validFiles.length) {
      return
    }

    setIsUploading(true)

    const filesWithRatio = await Promise.all(
      validFiles.map(async (file) => {
        try {
          const { width, height } = await getMediaDimensions(file)
          return { file, ratio: computeAspectRatio(width, height) }
        } catch {
          return { file, ratio: null }
        }
      }),
    )

    const cancelSource = axios.CancelToken.source()
    uploadCancelSourceRef.current = cancelSource

    const response = await uploadLibraryImages(filesWithRatio, { cancelToken: cancelSource.token })

    uploadCancelSourceRef.current = null

    if (response.cancelled) {
      return
    }

    setIsUploading(false)

    if (response.success) {
      addNotification({
        message: t('Library.upload.toastSuccess.title'),
        description: t('Library.upload.toastSuccess.description'),
      })
      libraryList.refetch()
      refreshCounts()
      return
    }

    addNotification({
      message: t('Library.upload.toastError.title'),
      description: t('Library.upload.toastError.description'),
      variant: 'error',
    })
  }

  return (
    <div className="library">
      <div className="library__header">
        <h1 className="main-title">{t('Library.PageTitle')}</h1>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,video/mp4,video/quicktime"
          multiple
          hidden
          onChange={handleFileChange}
        />

        <SecondaryButton
          text={t('Library.UploadButton')}
          color="navy"
          fullWidth={false}
          loading={isUploading}
          disabled={isUploading}
          onClick={handleUploadClick}
        />
      </div>

      <div className='library__content'>
        <div className='library__content-header'>
          <LibraryTabs
            activeTab={activeTab}
            handleTabChange={setActiveTab}
            refreshKey={countsRefreshKey}
          />

          <div className='right-side'>
            <SearchbarInput
              defaultValue={filters.search}
              handleChange={filters.handleSearch}
              clearSearchQuery={filters.clearSearchQuery}
              setClearSearchQuery={filters.setClearSearchQuery}
              placeholder={t('common.search')}
              ariaLabel={t('Events.search.ariaLabel')} 
              postfixView={
                <img
                  src={searchLogo}
                  className="searchbar-comp_icon"
                  alt={t('Events.search.placeholder')}
                />
              }
            />

            <div className='d-flex gap-3'>
              <FilterDropdown
                sort={filters.sort}
                ratios={filters.ratios}
                onSortChange={filters.handleSortChange}
                onRatioChange={filters.handleRatioChange}
                onReset={filters.handleFilterReset}
              />

              <TransparentBgButton
                text={selectiveMode ? t('common.cancel') : t('Library.select')}
                onClick={() => {
                  setSelectiveMode((prev) => !prev)
                }}
                color="navy"
              />
            </div>
          </div>
        </div>

        <LibraryList
          selectiveMode={selectiveMode}
          setSelectiveMode={setSelectiveMode}
          isUploading={isUploading}
          activeTab={activeTab}
          onLibraryMutated={refreshCounts}
          {...libraryList}
        />
      </div>
    </div>
  )
}

export default Library
